import { FileText } from "lucide-react";
import { useSearchParams } from "react-router";
import { StatusChip } from "../components/kit";
import { Alert } from "../components/ui";
import {
  day,
  Filter,
  fiscalOf,
  Loadable,
  money,
  PageHead,
  PRODUCT_LABEL,
  RestaurantLink,
  type Row,
  Section,
  SUB_LABEL,
  SubChip,
  Table,
  text,
  useAdmin,
  when,
} from "./ui";

// Abonamente și facturi: every invoice with its fiscal (SmartBill) state, the plans and the payment events.

const FISCAL_FILTERS: [string, string][] = [
  ["", "Toate facturile"],
  ["failed", "Refuzate de SmartBill"],
  ["pending", "În așteptare"],
  ["issued", "Emise"],
];

const EVENT_LABEL: Record<string, string> = {
  "invoice.paid": "Factură plătită",
  "invoice.payment_failed": "Plată eșuată",
  "customer.subscription.created": "Abonament creat",
  "customer.subscription.updated": "Abonament modificat",
  "customer.subscription.deleted": "Abonament încheiat",
  "checkout.session.completed": "Plată finalizată",
  "refund.requested": "Rambursare cerută (14 zile)",
};

const OUTCOME_LABEL: Record<string, string> = {
  invoice_paid: "plătită",
  invoice_failed: "plată eșuată",
  ignored: "ignorat",
  unknown_restaurant: "restaurant necunoscut",
  requested: "cerută",
};
const outcome = (o: unknown) => {
  const v = String(o ?? "");
  if (v.startsWith("subscription_")) return `abonament: ${SUB_LABEL[v.slice(13)]?.label ?? v.slice(13)}`;
  return OUTCOME_LABEL[v] ?? text(o);
};

export function Billing() {
  const [params, setParams] = useSearchParams();
  const fiscal = params.get("fiscal") ?? "";
  const invoices = useAdmin<{ invoices: Row[] }>(["invoices", fiscal], `/invoices?fiscal=${fiscal}`);
  const overview = useAdmin<{ paymentsEnabled: boolean; subscriptions: Row[]; events: Row[]; plans: Row[] }>(
    ["billing"],
    "/billing",
  );
  return (
    <>
      <PageHead
        title="Abonamente și facturi"
        sub="Plățile vin din Stripe; facturile fiscale se emit automat în SmartBill (și pleacă în e-Factura)."
      />
      <Loadable q={overview}>
        {(d) =>
          d.paymentsEnabled ? null : (
            <Alert tone="warning">
              Plățile online sunt oprite (BILLING_PROVIDER): nimeni nu poate plăti acum.
            </Alert>
          )
        }
      </Loadable>

      <Section
        title="Facturi"
        actions={
          <Filter
            label="Factură fiscală"
            value={fiscal}
            onChange={(v) => setParams(v ? { fiscal: v } : {}, { replace: true })}
            options={FISCAL_FILTERS}
          />
        }
      >
        <Loadable q={invoices}>
          {(d) => (
            <Table
              head={["Plătită", "Restaurant", "Produs", "Sumă", "Factură fiscală", "Documente"]}
              empty="Nicio factură."
              rows={d.invoices.map((i) => {
                const f = fiscalOf(i.fiscal_status);
                return [
                  when(i.paid_at ?? i.issued_at),
                  <RestaurantLink key="r" id={i.restaurant_id} name={i.restaurant} />,
                  i.product_kind
                    ? PRODUCT_LABEL[String(i.product_kind)]
                    : i.plan_interval === "year"
                      ? "Abonament anual"
                      : "Abonament lunar",
                  money(i.amount_minor, i.currency),
                  <span key="f" className="stack" style={{ gap: 2 }}>
                    <StatusChip
                      tone={f.tone}
                      icon={FileText}
                      label={i.fiscal_number ? `${text(i.fiscal_series)} ${text(i.fiscal_number)}` : f.label}
                    />
                    {i.fiscal_error ? (
                      <span className="caption">
                        {text(i.fiscal_error)} · {text(i.fiscal_attempts)} încercări
                      </span>
                    ) : null}
                  </span>,
                  <span key="d" className="row" style={{ gap: 8 }}>
                    {i.fiscal_url ? (
                      <a href={String(i.fiscal_url)} target="_blank" rel="noreferrer">
                        SmartBill
                      </a>
                    ) : null}
                    {i.hosted_url ? (
                      <a href={String(i.hosted_url)} target="_blank" rel="noreferrer">
                        Stripe
                      </a>
                    ) : null}
                  </span>,
                ];
              })}
            />
          )}
        </Loadable>
        <p className="caption muted" style={{ margin: 0 }}>
          Facturile refuzate se reîncearcă automat în fiecare noapte. Rambursarea în 14 zile se face din
          Stripe, iar stornarea din SmartBill (pașii din docs/PASI_PROPRIETAR.md).
        </p>
      </Section>

      <Loadable q={overview}>
        {(d) => (
          <>
            <Section title="Abonamente">
              <Table
                head={["Restaurant", "Stare", "Plan", "Proba până la", "Plătit până la"]}
                rows={d.subscriptions.map((r) => [
                  <RestaurantLink key="r" id={r.id} name={r.name} />,
                  <SubChip key="s" status={r.status} trialEndsAt={r.trial_ends_at} />,
                  r.plan_id ? text(r.plan_id) : "—",
                  day(r.trial_ends_at),
                  `${day(r.current_period_end)}${Number(r.cancel_at_period_end) ? " · se anulează" : ""}`,
                ])}
              />
            </Section>
            <Section title="Planuri">
              <Table
                head={["Plan", "Interval", "Preț de listă"]}
                rows={d.plans.map((p) => [
                  text(p.name),
                  p.interval === "year" ? "anual" : "lunar",
                  p.price_minor ? money(p.price_minor, p.currency) : "—",
                ])}
              />
              <p className="caption muted" style={{ margin: 0 }}>
                Prețul în lei și reducerea din primul an sunt în Stripe (cupoanele MENIOVA_PRIMUL_AN*).
              </p>
            </Section>
            <Section title="Evenimente de plată (ultimele 50)">
              <Table
                head={["Primit", "Eveniment", "Restaurant", "Rezultat"]}
                rows={d.events.map((e) => {
                  const name = d.subscriptions.find((s) => s.id === e.restaurant_id)?.name;
                  return [
                    when(e.received_at),
                    EVENT_LABEL[String(e.type)] ?? text(e.type),
                    e.restaurant_id ? (
                      <RestaurantLink key="r" id={e.restaurant_id} name={name ?? "restaurant"} />
                    ) : (
                      "—"
                    ),
                    outcome(e.outcome),
                  ];
                })}
              />
            </Section>
          </>
        )}
      </Loadable>
    </>
  );
}
